import {
    Component,
    Element,
    h,
    Host,
    Listen,
    Prop,
    State,
} from "@stencil/core";
import { debounce } from "../../utils/debounce";

interface TableCropping {
    left: number;
    right: number;
    top: number;
    bottom: number;
}

/**
 * @slot (default) - Slot for the native table
 */
@Component({
    tag: "ux-iws-comparison-table",
    styleUrl: "comparison-table.scss",
    shadow: false,
})
export class ComparisonTable {
    private baseClass = "ux-iws-comparison-table";

    @Element() element: HTMLUxIwsComparisonTableElement;

    /**
     * Style variant of the table
     */
    @Prop() readonly variant: "content" | "data" = "content";

    /**
     * Set to `true` if the table should use the full available space
     */
    @Prop() readonly fullWidth: boolean = false;

    /**
     * Set to `true` if the table header should be displayed sticky at the top of the viewport
     */
    @Prop() readonly stickyHeader: boolean = false;

    /**
     * Set to `true` if the table row headers should be displayed sticky at the left of the viewport
     */
    @Prop() readonly stickyRowHeader: boolean = false;

    /** Storage for the cropping values */
    @State() cropping: TableCropping = { left: 0, right: 0, top: 0, bottom: 0 };

    @Listen("resize", { target: "window" })
    handleResize() {
        this.updateCroppingDebounced();
    }

    @Listen("scroll", { target: "document" })
    handleScroll() {
        this.updateCropping();
    }

    componentWillLoad() {
        this.tableElement?.removeAttribute("class");
    }

    componentDidLoad() {
        this.updateCroppingDebounced();
    }

    /** Get the native table element */
    private get tableElement(): HTMLTableElement | null {
        return this.getTableElement();
    }

    /** Storage for the calculated table header height */
    private headerHeight: number = 0;

    /** Storage for the calculated row header width */
    private rowHeaderWidth: number = 0;

    /** Returns the native table element independent of hydration state */
    private getTableElement = (): HTMLTableElement | null => {
        const container = this.element.querySelector(
            `${this.baseClass}__table`
        );
        return container
            ? container.querySelector<HTMLTableElement>("table")
            : this.element.querySelector<HTMLTableElement>("table");
    };

    /** Calculate cropping based on the current viewport size and scroll position */
    private updateCropping = () => {
        const table = this.tableElement;
        if (table) {
            const viewPort = {
                width: document.documentElement.clientWidth,
                height: document.documentElement.clientHeight,
            };
            const elementRect = this.element.getBoundingClientRect();
            const tableRect = table.getBoundingClientRect();
            this.cropping = {
                left: Math.max(0, elementRect.left - tableRect.left),
                right: Math.max(0, tableRect.right - elementRect.right),
                top: Math.max(0, -elementRect.top),
                bottom: Math.max(0, viewPort.height - elementRect.bottom),
            };
        }
    };

    /** Debounced instance of the updateCropping method */
    private updateCroppingDebounced = debounce(this.updateCropping, 10);

    /** Handle table scroll events */
    private onScrollTable = (event: Event) => {
        event.preventDefault();
        this.updateCropping();
    };

    /** Calculate the height of the sticky table header */
    private calculateTableHeaderHeight = (): number => {
        const table = this.tableElement;
        if (!table) {
            this.headerHeight = 0;
        } else {
            const thead = table.querySelector("thead");
            this.headerHeight = thead
                ? thead.getBoundingClientRect().height
                : 0;
        }
        return this.headerHeight;
    };

    /** Calculate the width of the sticky row headers */
    private calculateTableRowHeaderWidth = (): number => {
        const tbody = this.tableElement?.querySelector("tbody");
        if (!tbody) {
            this.rowHeaderWidth = 0;
        } else {
            const cells = Array.from(
                tbody.querySelectorAll<
                    HTMLTableCellElement | HTMLTableHeaderCellElement
                >("th:first-child, td:first-child")
            );
            this.rowHeaderWidth = cells.reduce<number>(
                (previousValue, cell) => {
                    if (cell.colSpan > 1) {
                        return previousValue;
                    }
                    return Math.max(
                        previousValue,
                        cell.getBoundingClientRect().width
                    );
                },
                0
            );
        }
        return this.rowHeaderWidth;
    };

    /** Render the sticky table headers */
    private renderTableHeaders = () => {
        const table = this.tableElement;
        if (!table) {
            return null;
        }

        this.calculateTableHeaderHeight();
        const headerHeight = this.headerHeight;

        // const elementRect = this.element.getBoundingClientRect();
        const tableRect = table.getBoundingClientRect();
        const headerCells = Array.from(
            table.querySelectorAll<
                HTMLTableHeaderCellElement | HTMLTableCellElement
            >("thead th, thead td")
        );
        const headerCellRects = headerCells.map((th) =>
            th.getBoundingClientRect()
        );

        const renderCells = () => {
            return headerCells.map((th, index) => {
                const cellRect = headerCellRects[index];
                return (
                    <th
                        key={`table-header-${index}-${th}`}
                        style={{
                            width: `${cellRect.width}px`,
                            height: `${headerHeight}px`,
                        }}
                        innerHTML={th.innerHTML}
                    />
                );
            });
        };

        return (
            <div class={`${this.baseClass}__sticky-top`}>
                <table
                    class={{
                        [`${this.baseClass}__header`]: true,
                        [`${this.baseClass}__header--sticky`]:
                            this.cropping.top > 0,
                    }}
                    style={{
                        width: `${tableRect.width}px`,
                        transform: `translateX(-${this.cropping.left}px)`,
                    }}
                >
                    <thead>
                        <tr>{renderCells()}</tr>
                    </thead>
                </table>
            </div>
        );
    };

    /** Render the sticky table row headers */
    private renderTableRowHeaders = () => {
        const table = this.tableElement;
        if (!table) {
            return null;
        }

        this.calculateTableHeaderHeight();
        this.calculateTableRowHeaderWidth();
        const rowHeaderWidth = this.rowHeaderWidth;

        const tableRect = table.getBoundingClientRect();
        const tableRows = Array.from(
            table.querySelectorAll<HTMLTableRowElement>("tbody tr")
        );
        const renderRows = () => {
            return tableRows.map((tr, index) => {
                const rowRect = tr.getBoundingClientRect();
                const headerCell = Array.from(
                    tr.querySelectorAll<
                        HTMLTableCellElement | HTMLTableHeaderCellElement
                    >("th, td")
                )[0];
                if (!headerCell) {
                    return null;
                }
                const cellY = rowRect.top - tableRect.top - this.headerHeight;
                const cellWidth = Math.max(
                    rowHeaderWidth,
                    headerCell.getBoundingClientRect().width
                );
                return (
                    <tr
                        key={`table-row-header-${index}-${tr}`}
                        class={tr.className}
                    >
                        <th
                            style={{
                                height: `${rowRect.height}px`,
                                top: `${cellY}px`,
                                width: `${cellWidth}px`,
                            }}
                            innerHTML={headerCell.innerHTML}
                        />
                    </tr>
                );
            });
        };

        return (
            <table
                class={{
                    [`${this.baseClass}__row-header`]: true,
                    [`${this.baseClass}__row-header--sticky`]:
                        this.cropping.left > 0,
                }}
                style={{
                    top: `${this.headerHeight}px`,
                    width: `${rowHeaderWidth}px`,
                }}
            >
                <thead>
                    <tr>
                        <td
                            style={{
                                height: `${this.headerHeight}px`,
                            }}
                        >
                            &nbsp;
                        </td>
                    </tr>
                </thead>
                <tbody>{renderRows()}</tbody>
            </table>
        );
    };

    render() {
        return (
            <Host
                class={{
                    [this.baseClass]: true,
                    [`${this.baseClass}--${this.variant}`]: !!this.variant,
                    [`${this.baseClass}--full-width`]: this.fullWidth,
                }}
            >
                <div
                    class={`${this.baseClass}__table`}
                    onScroll={this.onScrollTable}
                >
                    <slot />
                </div>
                {this.stickyRowHeader && (
                    <div
                        class={`${this.baseClass}__overlay ${this.baseClass}__overlay--cropped`}
                    >
                        {this.renderTableRowHeaders()}
                    </div>
                )}
                {this.stickyHeader && (
                    <div class={`${this.baseClass}__overlay`}>
                        {this.renderTableHeaders()}
                    </div>
                )}
                <div class={`${this.baseClass}__overlay--cropped`}>
                    <div
                        class={{
                            [`${this.baseClass}__gradient`]: true,
                            [`${this.baseClass}__gradient--left`]: true,
                            [`${this.baseClass}__gradient--cropped`]:
                                this.cropping.left > 2,
                        }}
                        aria-hidden="true"
                    />
                    <div
                        class={{
                            [`${this.baseClass}__gradient`]: true,
                            [`${this.baseClass}__gradient--right`]: true,
                            [`${this.baseClass}__gradient--cropped`]:
                                this.cropping.right > 2,
                        }}
                        aria-hidden="true"
                    />
                </div>
            </Host>
        );
    }
}
